// ./src/contexts/CarsContext/CarsContext.tsx
//
// Context wrapper around the useCars hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, toolbar)
//   to share the same cars state without prop-drilling.
// - Multiple providers can coexist:
//     • Global provider in App.tsx with active-only filter (for CarSelector, dashboard)
//     • Page-level provider in CarsPage with no status filter (for car management)
// - The underlying useCars hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In App.tsx (global, active cars only — used by CarSelector, dashboard):
//   <CarsProvider>
//     ...
//   </CarsProvider>
//
//   // In CarsPage (all cars for management):
//   <CarsProvider options={{ initialFilters: {} }}>
//     <CarsPageContent />
//     <CarEditDrawer />
//   </CarsProvider>
//
//   // In any child component:
//   const { cars, loading, create, ... } = useCarsContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalCarsContext();
//   const createFn = context?.create ?? props.onCreate;

import React, { createContext, useContext, useMemo, useEffect } from "react";

import { CAR_STATUSES } from "@/config";

import { Car } from "@/utils/api/CarService";

import { useCars, UseCarsReturn, UseCarsOptions } from "@/hooks/useCars";

import { useEntityFiltersContext } from "@/contexts/EntityFiltersContext";


// ============================================
// Extended Context Value
// ============================================

export interface CarsContextValue extends UseCarsReturn {
  // Computed values
  totalCarsCount: number;
  hasMultipleCars: boolean;
  hasCars: boolean;

  // Quick access
  firstCar: Car | null;
}

// ============================================
// Context
// ============================================

const CarsContext = createContext<CarsContextValue | null>(null);

// ============================================
// Provider
// ============================================

export interface CarsProviderProps {
  children: React.ReactNode;
  /** Options for the cars hook (autoLoad, initialFilters, initialSort, pageSize) */
  options?: UseCarsOptions;
}

/**
 * Wraps useCars into a context provider.
 *
 * Each instance creates its own independent useCars store
 * with its own filters, pagination, loading state, and error state.
 *
 * The global provider (in App.tsx) uses the default active-only filter.
 * Page-level providers can override filters for their use case.
 *
 * @example
 * // Global (active cars for selectors):
 * <CarsProvider>
 *   <App />
 * </CarsProvider>
 *
 * // CarsPage (all cars for management):
 * <CarsProvider options={{ initialFilters: {} }}>
 *   <CarsPageContent />
 * </CarsProvider>
 */
export const CarsProvider: React.FC<CarsProviderProps> = ({
  children,
  options = {},
}) => {
  // Access entity filters context for auto-selection
  const { filters: entityFilters, setCarId } = useEntityFiltersContext();

  // Default options: autoLoad=true, filter by active status
  const mergedOptions: UseCarsOptions = {
    autoLoad: true,
    initialFilters: { status: [CAR_STATUSES.ACTIVE] },
    ...options,
  };

  const carsHook = useCars(mergedOptions);

  const { cars, loading } = carsHook;

  // ============================================
  // Computed values
  // ============================================

  const totalCarsCount = useMemo(() => cars.length, [cars]);
  const hasMultipleCars = useMemo(() => cars.length > 1, [cars]);
  const hasCars = useMemo(() => cars.length > 0, [cars]);
  const firstCar = useMemo(
    () => (cars.length > 0 ? cars[0] : null),
    [cars]
  );

  // ============================================
  // Auto-select single active car
  // ============================================

  useEffect(() => {
    // Wait until cars have finished loading
    if (loading) return;

    // Only auto-select when there's exactly one car
    if (cars.length !== 1) return;

    // Check if current selection is valid (exists in loaded cars)
    const currentSelectionValid =
      entityFilters.carId &&
      cars.some((c) => c.id === entityFilters.carId);

    // Auto-select if no car selected or current selection is invalid
    if (!currentSelectionValid && cars[0].id) {
      setCarId(cars[0].id);
    }
  }, [loading, cars, entityFilters.carId, setCarId]);

  // ============================================
  // Context value
  // ============================================

  const contextValue = useMemo<CarsContextValue>(
    () => ({
      ...carsHook,
      totalCarsCount,
      hasMultipleCars,
      hasCars,
      firstCar,
    }),
    [carsHook, totalCarsCount, hasMultipleCars, hasCars, firstCar]
  );

  return (
    <CarsContext.Provider value={contextValue}>{children}</CarsContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the CarsContext. Throws if used outside a CarsProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. CarSelector, CarsList, DashboardVehicleGrid).
 */
export const useCarsContext = (): CarsContextValue => {
  const context = useContext(CarsContext);

  if (!context) {
    throw new Error(
      "useCarsContext must be used within a <CarsProvider>. " +
      "Wrap your component tree with <CarsProvider> or use useOptionalCarsContext() instead."
    );
  }

  return context;
};

/**
 * Access the CarsContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. CarEditDrawer which works both inside CarsPage's provider
 * and standalone on other pages).
 *
 * @example
 * const context = useOptionalCarsContext();
 * const createFn = context?.create ?? props.onCreate;
 * const loadFn = context?.get ?? props.onLoad;
 */
export const useOptionalCarsContext = (): CarsContextValue | null => {
  return useContext(CarsContext);
};